<div class="index-shell">
    <app-header [breadcrumbs]="['System settings', 'Data upload']"
                [links]="['/settings/general-information']"
                mainHeading="Data upload">
            
                <div headingButton class="data-upload-buttons">
                    <input
                    type="file"
                    accept=".csv,text/csv"
                    (change)="onFileSelected($event)"
                    id="csvFileInput"
                    #fileInput
                    [disabled]="isFileLoaded()"
                    style="display: none;">

                    @if (!fileName()) {
                        <button cll-filled-button
                            colour="primary"
                            (click)="fileInput.click()">
                            <cll-icon type="outlined">attach_file</cll-icon>
                            Choose CSV File
                        </button>

                        <button cll-filled-button
                            colour="secondary"
                            (click)="downloadTemplate()">
                            <cll-icon type="outlined">download</cll-icon>
                            Download template
                        </button>
                    }

                    @if (fileName()) {
                        <button cll-filled-button
                            colour="primary"
                            [disabled]="isUploading()"
                            (click)="uploadPackageData()">
                            <cll-icon type="outlined">{{ isUploading() ? 'hourglass_empty' : 'cloud_upload' }}</cll-icon>
                            {{ isUploading() ? 'Uploading...' : 'Upload Package Data' }}
                        </button>
        
                        <button cll-filled-button
                            colour="variant"
                            [disabled]="isUploading()"
                            (click)="clearData()">
                            <cll-icon type="outlined">clear</cll-icon>
                            Clear Data
                        </button>
                    }
                </div>
            </app-header>

    <div class="upload-container">
        <!-- Column Requirements Section -->
        @if (uploadedPackages().length === 0) {
            <cll-collapsible-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">info</cll-icon>
                    <cll-card-title>
                        CSV Column Requirements
                    </cll-card-title>
                    <cll-card-subtitle>
                        View detailed validation rules and examples for each column
                    </cll-card-subtitle>
                </cll-card-header>

            <div class="grid-wrapper">
                <div class="columns-grid">
                @for (config of columnConfigs(); track config.name) {
                    <div class="column-item" [class.required]="config.required">
                        <div class="column-header">
                            <strong>{{ config.name }}</strong>
                            @if (config.required) {
                                <span class="required-badge">Required</span>
                            } @else {
                                <span class="optional-badge">Optional</span>
                            }
                        </div>
                        <div class="column-comment">{{ config.comment }}</div>

                        <div class="validation-info">
                            <div class="validation-label">Validation Rules</div>
                            <div class="validation-rule">{{ config.validation }}</div>
                        </div>

                        <div class="validation-info examples-section">
                            <div class="validation-label">Examples</div>
                            <div class="validation-rule example-text">{{ config.example }}</div>
                        </div>

                        @if (!config.required && config.defaultValue !== undefined) {
                            <div class="default-value">Default: {{ config.defaultValue }}</div>
                        }
                    </div>
                }
                </div>
            </div>
        </cll-collapsible-card>
        }

        <!-- Debug: CSV Data State -->
        <!--
        CSV Data Length: {{ csvData().length }}
        Is Loading: {{ isLoading() }}
        Is Valid CSV: {{ isValidCsv() }}
        Uploaded Packages: {{ uploadedPackages().length }}
        -->

        <!-- Uploaded Packages Success Table -->
        @if (uploadedPackages().length > 0) {
            <cll-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">check_circle</cll-icon>
                    <cll-card-title>Data Uploaded Successfully</cll-card-title>
                    <cll-card-subtitle>{{ uploadedPackages().length }} package(s) have been successfully uploaded to the system</cll-card-subtitle>
                </cll-card-header>

                <div class="uploaded-packages-table">
                    <table class="cll-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Package Reference</th>
                                <th>Package ID</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            @for (pkg of uploadedPackages(); track pkg.id) {
                                <tr>
                                    <td>{{ $index + 1 }}</td>
                                    <td>{{ pkg.reference }}</td>
                                    <td>{{ pkg.id }}</td>
                                    <td>
                                        <button cll-outline-button
                                            colour="secondary"
                                            (click)="viewPackage(pkg.id)">
                                            <cll-icon type="outlined">visibility</cll-icon>
                                            View Package
                                        </button>
                                    </td>
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>

                <div class="success-actions">
                    <button cll-filled-button
                        colour="secondary"
                        (click)="clearData()">
                        <cll-icon type="outlined">add</cll-icon>
                        Start New Upload
                    </button>
                </div>
            </cll-card>
        }

        <!-- Validation Errors -->
        @if (validationErrors().length > 0 && uploadedPackages().length === 0) {
            <cll-card colour="error">
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">error_outline</cll-icon>
                    <cll-card-title>Validation Errors</cll-card-title>
                    <cll-card-subtitle>Please fix these issues and upload the file again.</cll-card-subtitle>
                </cll-card-header>

                <div class="validation-errors">
                    <ul>
                        @for (error of validationErrors(); track $index) {
                            <li>{{ error }}</li>
                        }
                    </ul>
                </div>

                <div class="validation-error-actions">
                    <button cll-filled-button
                        colour="secondary"
                        (click)="clearData()">
                        <cll-icon type="outlined">refresh</cll-icon>
                        Clear and Try Again
                    </button>
                </div>
            </cll-card>
        }

        <!-- Loading Spinner -->
        @if (isLoading() && uploadedPackages().length === 0) {
            <div class="loading-container">
                <cll-spinner />
                <p>Processing CSV file...</p>
            </div>
        }

        <!-- Data Table -->
        @if (csvData().length > 0 && !isLoading() && isValidCsv() && uploadedPackages().length === 0) {
            <div class="data-upload-container-omh">
                <div class="search-container-details">
                    <div class="tab-title-icon">
                        <cll-icon class="tab-icon" type="outlined">info</cll-icon>
                        <div class="accordion-sub-title-accordion">
                            <span class="detail-table-title">Data Management</span>
                            <span class="detail-table-sub-title">{{ csvData().length }} rows loaded - {{ fileName() }}</span>
                        </div>
                    </div>
                </div>
                <div class="tabs-table-container">
                    <table class="cll-table">
                        <thead>
                            <tr>
                                <th class="row-number-header">#</th>
                                @for (header of csvHeaders(); track header) {
                                    <th>{{ header }}</th>
                                }
                            </tr>
                        </thead>
                        <tbody>
                            @for (row of csvData(); track $index) {
                                <tr>
                                    <td class="row-number">{{ $index + 1 }}</td>
                                    @for (header of csvHeaders(); track header) {
                                        <td [class.error-cell]="isRequiredError(row[header])">
                                            <span class="cell-content"
                                                    [class.error-text]="isRequiredError(row[header])">
                                                {{ row[header] }}
                                            </span>
                                        </td>
                                    }
                                </tr>
                            }
                        </tbody>
                    </table>
                </div>
            </div>
        }

        <!-- Empty State -->
        @if (csvData().length === 0 && !isLoading() && uploadedPackages().length === 0 && validationErrors().length === 0) {
            <cll-card>
                <cll-card-header>
                    <cll-icon cllCardIcon type="outlined">cloud_upload</cll-icon>
                    <cll-card-title>No Data Loaded</cll-card-title>
                    <cll-card-subtitle>Upload a CSV file to view and manage your data.</cll-card-subtitle>
                </cll-card-header>

                <div class="empty-state">
                    <div class="empty-content">
                        <cll-icon class="empty-icon" type="outlined">cloud_upload</cll-icon>
                        <p>Select a CSV file using the 'Choose CSV File' button above to get started.</p>
                    </div>
                </div>
            </cll-card>
        }
    </div>
</div>
